import {
  forwardRef,
  type InputHTMLAttributes,
  type TextareaHTMLAttributes,
  type ReactNode,
} from 'react';

interface InputGroupProps {
  label?: string;
  error?: string;
  children: ReactNode;
}

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  error?: boolean;
}

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  error?: boolean;
}

interface CheckboxProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
}

export function InputGroup({ label, error, children }: InputGroupProps) {
  return (
    <div className="input-group">
      {label && <label className="input-label">{label}</label>}
      {children}
      {error && <span className="input-error text-body-xs text-stress">{error}</span>}
    </div>
  );
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className = '', error, ...props }, ref) => {
    return (
      <input
        ref={ref}
        className={`input ${error ? 'input-error' : ''} ${className}`.trim()}
        {...props}
      />
    );
  }
);

Input.displayName = 'Input';

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className = '', error, ...props }, ref) => {
    return (
      <textarea
        ref={ref}
        className={`input textarea ${error ? 'input-error' : ''} ${className}`.trim()}
        {...props}
      />
    );
  }
);

Textarea.displayName = 'Textarea';

export function Checkbox({ label, className = '', ...props }: CheckboxProps) {
  return (
    <label className={`checkbox-wrapper ${className}`.trim()}>
      <input type="checkbox" className="checkbox" {...props} />
      <span className="text-body-sm">{label}</span>
    </label>
  );
}

export function Radio({ label, className = '', ...props }: CheckboxProps) {
  return (
    <label className={`radio-wrapper ${className}`.trim()}>
      <input type="radio" className="radio" {...props} />
      <span className="text-body-sm">{label}</span>
    </label>
  );
}
